import React from 'react';
import { StandardTableActions, ColoredBadge, StatusBadgeStylePicker } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { TicketStatus } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { normalizeHexColor, parseStatusColorConfig, serializeStatusColorConfig } from '../../../utils/statusColor';

const DEFAULT_TICKET_STATUS_COLOR = '#64748b';

const getTicketStatusColor = (color?: string): string =>
  normalizeHexColor(parseStatusColorConfig(color).backgroundColor) ?? DEFAULT_TICKET_STATUS_COLOR;

const renderTicketStatusBadge = (label?: string, color?: string) => (
  <ColoredBadge label={label || '-'} color={color || DEFAULT_TICKET_STATUS_COLOR} />
);

export const moduleConfig = {
  module: {
    title: 'Ticket Status',
    description: 'Manage ticket status',
    permissionModuleKey: 'ticket_status',
    route: {
      list: '/ticket_status',
      new: '/ticket_status/new',
      detail: (id: number) => `/ticket_status/${id}`,
      edit: (id: number) => `/ticket_status/${id}/edit`,
    },
    api: {
      list: '/p-crm-ticket-status',
      detail: (id: number) => `/p-crm-ticket-status/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search ticket status...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pts_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pts_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: TicketStatus) => item.pts_descr,
        options: [],
      },
    ],
    applyFilter: (items: TicketStatus[], filters: { pts_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pts_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pts_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pts_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Ticket Statu',
        showTitle: false,
        variant: 'primary' as const,
        href: '/ticket_status/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: TicketStatus) => {
      window.location.href = `/ticket_status/${item.id}`;
    },
    keyExtractor: (item: TicketStatus) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new ticket statu.',
    noSearchResults: 'No ticket status found matching your search criteria.',
    noFilterResults: 'No ticket status match the selected filters.',
  },
  delete: {
    title: 'Delete Ticket Statu',
    message: 'Are you sure you want to delete this ticket statu? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete ticket statu. Please try again.',
    handler: async (id: number) => {
      const { deleteTicketStatus } = await import('../../../services/mutations');
      await deleteTicketStatus(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Ticket Statu Details',
    notFoundTitle: 'Ticket Statu not found',
    notFoundMessage: 'The ticket statu you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Ticket Status',
    defaultTitle: 'Unnamed Ticket Statu',
    getTitle: (item: TicketStatus) => {
      return item.pts_descr || `Ticket Statu #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Ticket Statu',
      subtitle: 'Add a new ticket statu',
      submitText: 'Create Ticket Statu',
    },
    edit: {
      title: 'Edit Ticket Statu',
      subtitle: 'Update ticket statu information',
      submitText: 'Update Ticket Statu',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading ticket statu...',
    errorMessage: 'Failed to save ticket statu. Please try again.',
    create: async (data: Partial<TicketStatus>) => {
      const { createTicketStatus } = await import('../../../services/mutations');
      return await createTicketStatus(data);
    },
    update: async (id: number, data: Partial<TicketStatus>) => {
      const { updateTicketStatus } = await import('../../../services/mutations');
      return await updateTicketStatus(id, data);
    },
  },
  fields: [
    {
      name: 'pts_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter ticket statu description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'pts_color',
      label: 'Badge Style',
      type: 'custom',
      required: true,
      helperText: 'Pick the background color and how the badge text should be rendered.',
      validation: {
        required: true,
      },
      customRender: ({ value, error, formData, setMultipleFieldValues }) => {
        const parsedConfig = parseStatusColorConfig(
          typeof value === 'string'
            ? value
            : serializeStatusColorConfig(DEFAULT_TICKET_STATUS_COLOR, 'auto'),
        );

        return (
          <StatusBadgeStylePicker
            backgroundColor={parsedConfig.backgroundColor}
            textColorMode={parsedConfig.textColorMode}
            error={error}
            helperText="This will be used in ticket status badges and ticket status selection."
            previewLabel={formData?.pts_descr || 'Preview'}
            onChange={(backgroundColor, textColorMode) => {
              setMultipleFieldValues?.({
                pts_color: serializeStatusColorConfig(backgroundColor, textColorMode),
              });
            }}
          />
        );
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Ticket Statu Information',
        icon: '📝',
        fields: ['pts_descr', 'pts_color'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<TicketStatus>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pts_descr',
    header: 'Description',
    sortable: true,
    render: (value, row) => renderTicketStatusBadge(value, row.pts_color),
  },
  {
    key: 'pts_color',
    header: 'Color',
    sortable: true,
    render: (_value, row) => {
      const resolvedColor = getTicketStatusColor(row.pts_color);
      return (
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: resolvedColor }} />
          <span className="text-sm text-gray-700">{resolvedColor}</span>
        </div>
      );
    },
  },
];

export const getDetailSections = (
  item: TicketStatus,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: renderTicketStatusBadge(item.pts_descr, item.pts_color),
        },
        {
          label: 'Color',
          value: (
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: getTicketStatusColor(item.pts_color) }} />
              <span>{getTicketStatusColor(item.pts_color)}</span>
            </div>
          ),
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: TicketStatus) => {
  return renderTicketStatusBadge(item.pts_descr, item.pts_color);
};

export const getInitialFormData = (): Partial<TicketStatus> => {
  return {
    pts_descr: '',
    pts_color: serializeStatusColorConfig(DEFAULT_TICKET_STATUS_COLOR, 'auto'),
  };
};

export const mapItemToFormData = (item: TicketStatus): Partial<TicketStatus> => {
  return {
    pts_descr: item.pts_descr || '',
    pts_color: item.pts_color || serializeStatusColorConfig(DEFAULT_TICKET_STATUS_COLOR, 'auto'),
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: TicketStatus,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

